/*
 * _customled-dealer-pricing.js  (Maranello M1.10.x — dealer tiered pricing display)
 *
 * Live MSRP / MAP / Dealer-price display + tier-table-driven computation for
 * the dealer-pricing block rendered by snippets/_customled-dealer-pricing.liquid.
 * No-ops if the wrapper element isn't on the page.
 *
 * Logic mirrors the Shopify discount Function so PDP display equals checkout
 * price. Tier data + per-product cap come from Liquid via data-* attributes
 * on the wrapper.
 *
 * Reactivity:
 *   - On load: render Dealer price for initial variant + qty=1
 *   - On Maranello's quantity input change: re-render Dealer price
 *   - On PUB_SUB_EVENTS.productVariantChange: update MSRP, MAP, Dealer price
 *     from the new variant + re-attach the qty listener since the qty input
 *     gets replaced via outerHTML during Maranello's variant-change pipeline
 *   - M1.21 SAFETY NET: a MutationObserver on the stable shopify-section
 *     container re-runs processAll whenever the wrapper is REPLACED by a
 *     fresh SSR render. Maranello swaps the whole block on variant change
 *     and the PUB_SUB event alone proved unreliable (the swap can land after
 *     our deferred handler ran, leaving the price stuck at the SSR "—"
 *     placeholder with dead qty listeners). Same stable-container lesson as
 *     the relay-card observer fix (M1.16).
 *
 * Localization (M1.21): the rendered strings come from data-l10n-* attrs on
 * the wrapper (set from locale keys in _customled-dealer-pricing.liquid)
 * with EN fallbacks; tokens [PRICE] [PCT] [QTY] are substituted at render.
 *
 * Subscriber registration uses a short polling loop to handle the (rare)
 * case where this script runs before scripts.js initializes
 * `subscribe` / `PUB_SUB_EVENTS`. Caps at ~3s of total wait time.
 */
(function () {
  "use strict";

  var WRAPPER_SELECTOR = "[data-customled-dealer-pricing]";

  function parseTierTable(jsonStr) {
    try {
      var parsed = JSON.parse(jsonStr);
      if (!Array.isArray(parsed)) return [];
      return parsed.slice().sort(function (a, b) { return a.minQty - b.minQty; });
    } catch (e) {
      return [];
    }
  }

  function lookupTier(tiers, quantity) {
    var pct = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (quantity >= tiers[i].minQty) {
        pct = tiers[i].pct;
      } else {
        break;
      }
    }
    return pct;
  }

  function applyCap(tierPct, capValue) {
    if (capValue === null) return tierPct;
    return Math.min(tierPct, capValue);
  }

  function formatMoney(cents) {
    var amount = (cents || 0) / 100;
    try {
      var currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency
      }).format(amount);
    } catch (e) {
      return "$" + amount.toFixed(2);
    }
  }

  function getCurrentQuantity(scope) {
    var section = scope.closest("[id^='shopify-section-']") || document;
    var qtyInput = section.querySelector('product-form input[name="quantity"]');
    if (!qtyInput) {
      qtyInput = section.querySelector('input[name="quantity"]');
    }
    if (!qtyInput) return 1;
    var n = parseInt(qtyInput.value, 10);
    return isFinite(n) && n > 0 ? n : 1;
  }

  function getCurrentPriceCents(scope) {
    var stored = scope.getAttribute("data-current-price-cents");
    if (stored) {
      var n = parseInt(stored, 10);
      if (isFinite(n)) return n;
    }
    var init = parseInt(scope.getAttribute("data-init-price-cents"), 10);
    return isFinite(init) ? init : 0;
  }

  function renderDealerPrice(scope) {
    var priceEl = scope.querySelector("[data-customled-dealer-price]");
    var metaEl = scope.querySelector("[data-customled-dealer-meta]");
    if (!priceEl) return;

    var tiers = parseTierTable(scope.getAttribute("data-tier-json"));
    if (!tiers.length) {
      priceEl.textContent = "—";
      if (metaEl) metaEl.textContent = "";
      return;
    }

    var capRaw = scope.getAttribute("data-cap");
    var capValue = (capRaw === null || capRaw === "") ? null : parseInt(capRaw, 10);
    if (capValue !== null && !isFinite(capValue)) capValue = null;

    var qty = getCurrentQuantity(scope);
    var basePriceCents = getCurrentPriceCents(scope);

    var tierPct = lookupTier(tiers, qty);
    var finalPct = applyCap(tierPct, capValue);

    var discountedCents = Math.round(basePriceCents * (1 - finalPct / 100));
    var eachTpl = l10n(scope, "data-l10n-each", "[PRICE] each");
    priceEl.textContent = eachTpl.replace("[PRICE]", formatMoney(discountedCents));

    if (metaEl) {
      if (finalPct === 0) {
        metaEl.textContent = l10n(scope, "data-l10n-meta-none", "(no dealer discount)");
      } else {
        metaEl.textContent = l10n(scope, "data-l10n-meta", "([PCT]% off, qty [QTY])")
          .replace("[PCT]", String(finalPct))
          .replace("[QTY]", String(qty));
      }
    }
  }

  function l10n(scope, attr, fallback) {
    var v = scope.getAttribute(attr);
    return v && v.trim() ? v : fallback;
  }

  function renderMsrpAndMap(scope, priceCents, compareCents) {
    var mapEl = scope.querySelector("[data-customled-dealer-map]");
    if (mapEl) {
      mapEl.textContent = formatMoney(priceCents);
    }

    var msrpRow = scope.querySelector("[data-customled-dealer-msrp-row]");
    var msrpEl = scope.querySelector("[data-customled-dealer-msrp]");
    var hasCompare = compareCents && compareCents > priceCents;
    if (msrpRow) {
      if (hasCompare) {
        msrpRow.removeAttribute("hidden");
      } else {
        msrpRow.setAttribute("hidden", "");
      }
    }
    if (msrpEl && hasCompare) {
      msrpEl.textContent = formatMoney(compareCents);
    }
  }

  // Process every dealer-pricing wrapper on the page. Re-runs are safe
  // (idempotent) so this can be called on initial load AND on variant
  // change events.
  function processAll(opts) {
    var wrappers = document.querySelectorAll(WRAPPER_SELECTOR);
    wrappers.forEach(function (wrapper) {
      if (opts && opts.variant) {
        var newPrice = typeof opts.variant.price === "number" ? opts.variant.price : 0;
        var newCompare = typeof opts.variant.compare_at_price === "number" ? opts.variant.compare_at_price : 0;
        wrapper.setAttribute("data-current-price-cents", String(newPrice));
        wrapper.setAttribute("data-current-compare-cents", String(newCompare));
        renderMsrpAndMap(wrapper, newPrice, newCompare);
      }
      renderDealerPrice(wrapper);
      attachQuantityListener(wrapper);
      wrapper.setAttribute("data-cl-processed", "1");
    });
  }

  // M1.21: re-process when Maranello SSR-swaps the wrapper (variant change).
  // Observe the STABLE section container — the wrapper itself gets destroyed.
  // A freshly swapped-in wrapper lacks data-cl-processed; that's the signal.
  function initSectionObserver() {
    var wrapper = document.querySelector(WRAPPER_SELECTOR);
    if (!wrapper) return;
    var target = wrapper.closest("[id^='shopify-section-']") || document.body;
    if (target.__customledDealerObserved) return; // idempotent
    target.__customledDealerObserved = true;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      if (!document.querySelector(WRAPPER_SELECTOR + ":not([data-cl-processed])")) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        processAll();
      }, 30);
    }).observe(target, { childList: true, subtree: true });
  }

  function attachQuantityListener(wrapper) {
    var section = wrapper.closest("[id^='shopify-section-']") || document;
    var qtyInput = section.querySelector('product-form input[name="quantity"]');
    if (!qtyInput) {
      qtyInput = section.querySelector('input[name="quantity"]');
    }
    if (!qtyInput) return;
    if (qtyInput.__customledDealerPricingBound) return; // idempotent
    qtyInput.__customledDealerPricingBound = true;
    qtyInput.addEventListener("input", function () { renderDealerPrice(wrapper); });
    qtyInput.addEventListener("change", function () { renderDealerPrice(wrapper); });
  }

  function handleVariantChange(event) {
    var variant = event && event.variant;
    if (!variant) return;
    // Defer slightly so Maranello's outerHTML swap of the variant-changed-
    // update block (which contains our wrapper) completes before we query
    // for the new wrapper.
    setTimeout(function () {
      processAll({ variant: variant });
    }, 0);
  }

  // Poll for `subscribe` + `PUB_SUB_EVENTS` to be available, then register.
  // Defensive against any timing edge case where this script evaluates
  // before scripts.js has set them up.
  function tryRegisterSubscriber(attempts) {
    var attempt = attempts || 0;
    var hasSubscribe = false;
    var hasEvents = false;
    try {
      hasSubscribe = typeof subscribe === "function";
      hasEvents = typeof PUB_SUB_EVENTS === "object" && PUB_SUB_EVENTS && PUB_SUB_EVENTS.productVariantChange;
    } catch (e) {
      // Reference error from TDZ — treat as not-yet-available.
    }
    if (hasSubscribe && hasEvents) {
      subscribe(PUB_SUB_EVENTS.productVariantChange, handleVariantChange);
      return;
    }
    if (attempt < 50) {
      setTimeout(function () { tryRegisterSubscriber(attempt + 1); }, 60);
    }
  }

  function init() {
    processAll();
    tryRegisterSubscriber(0);
    initSectionObserver();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
